// src/services/pdfQuote.js
// Generador de PDF para cotizaciones con:
// - Encabezado con logo y datos
// - Bloque derecho con: COTIZACIÓN + No. + Fecha
// - Línea divisoria muy discreta debajo del encabezado
// - Tabla de productos (# primero, luego IMG)
// - Totales
// - Condiciones
// - Botones azul y naranja
// - Redes con iconos (incluye WhatsApp)
// - Ajustado para que cotizaciones pequeñas queden en 1 sola página

import jsPDF from 'jspdf';

// ⚠️ Cambia si tu logo no es PNG
import logoDasilva from '../assets/logo.png';

// =====================================================
//  HELPERS IMÁGENES
// =====================================================

function normalizeImageUrl(url) {
  if (!url) return null;
  let clean = String(url).trim();

  if (clean.startsWith('data:')) return clean;

  if (clean.includes('drive.google.com')) {
    const byD = clean.match(/\/d\/([^/]+)/);
    const byId = clean.match(/[?&]id=([^&]+)/);
    const id = (byD && byD[1]) || (byId && byId[1]);
    if (id) {
      clean = `https://drive.google.com/uc?export=view&id=${id}`;
    }
  }

  const isHttp =
    clean.startsWith('http://') || clean.startsWith('https://');
  if (!isHttp) return clean;

  const lower = clean.toLowerCase();
  const isLocal =
    lower.includes('localhost') ||
    lower.includes('127.0.0.1') ||
    /https?:\/\/(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1]))/.test(lower);

  if (isLocal) return clean;

  const noProtocol = clean.replace(/^https?:\/\//, '');
  return `https://images.weserv.nl/?url=${encodeURIComponent(noProtocol)}`;
}

async function urlToDataURL(url) {
  const finalUrl = normalizeImageUrl(url);
  if (!finalUrl) return null;

  try {
    const res = await fetch(finalUrl, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function detectImageType(dataUrl) {
  if (!dataUrl) return 'JPEG';
  const mime = (dataUrl.match(/^data:(image\/[^;]+)/i) || [])[1] || '';
  if (mime.includes('png')) return 'PNG';
  return 'JPEG';
}

// =====================================================
//  HELPERS GENERALES
// =====================================================

function formatMoney(v) {
  const n = Number(v) || 0;
  return '$ ' + n.toLocaleString('es-CO');
}

function writeParagraph(doc, text, x, y, maxWidth, lineHeight = 5) {
  const lines = doc.splitTextToSize(String(text), maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function ensureSpace(doc, y, needed, marginTop = 20) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed >= pageHeight - 25) {
    doc.addPage();
    return marginTop;
  }
  return y;
}

// =====================================================
//  FUNCIÓN PRINCIPAL
// =====================================================

export async function downloadQuotePDF(payload) {
  if (!payload) return;

  const {
    quoteNumber,
    date,
    eventDateDisplay,
    client = {},
    items = [],
    subtotal,
    discountPercent = 0,
    discountAmount  = 0,
    extraCost,
    extraHours,
    ivaPercent,
    ivaAmount,
    transport = {},
    total,
    notes,
    schedule = {},
    company = {},
    conditions = [],
  } = payload;

  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 18;

  const PRIMARY = { r: 99, g: 102, b: 241 };
  const SECONDARY = { r: 249, g: 115, b: 22 };

  // =====================================================
  //  ENCABEZADO (LOGO + DATOS / DERECHA: COT + No + Fecha)
  // =====================================================

  try {
    const logoData = await urlToDataURL(logoDasilva);
    if (logoData) {
      doc.addImage(logoData, detectImageType(logoData), margin, y - 5, 26, 26);
    }
  } catch {}

  // Datos empresa — siempre constantes, nunca del payload
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Inflables Da Silva', margin + 32, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Chía, Cundinamarca, Colombia', margin + 32, y + 6);
  doc.text('Tel: +57 3334008723 - 3112952770', margin + 32, y + 12);
  doc.text('Email: contacto@inflablesdasilva.com', margin + 32, y + 18);
  doc.text('Web: www.inflablesdasilva.com | www.dasilvafunpark.com', margin + 32, y + 24);

  // Derecha (COT + No + Fecha)
  const rightX = pageWidth - margin;
  const headerTopY = y;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('COTIZACIÓN', rightX, headerTopY, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`No.: ${quoteNumber}`, rightX, headerTopY + 6, { align: 'right' });
  doc.text(`Fecha: ${date}`, rightX, headerTopY + 12, { align: 'right' });

  // BAJAR un poco más
  y = headerTopY + 30;

  // =====================================================
  //  LÍNEA DIVISORIA MUY DISCRETA
  // =====================================================

  doc.setLineWidth(0.2);
  doc.setDrawColor(210, 210, 210);
  doc.line(margin + 5, y, pageWidth - margin - 5, y);

  y += 6;

 // =====================================================
  //  BLOQUES CLIENTE / EVENTO (MEJORADO: CONTACTO + DIRECCIÓN)
  // =====================================================

  const midX = pageWidth / 2;
  const startY = y; // Guardamos la altura inicial para alinear columnas

  // --- COLUMNA IZQUIERDA: CLIENTE ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Cliente', margin, y);
  
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

 // 1. Nombre (Empresa o Persona)
  doc.text(`Nombre: ${client.name || ''}`, margin, y);
  y += 5;

  // 2. Empresa (si existe y es diferente a Particular)
  if (client.company && client.company !== 'Particular') {
    doc.text(`Empresa: ${client.company}`, margin, y);
    y += 5;
  }

  // 3. Persona de Contacto (Solo si existe, ej: Empresas)
  if (client.contactPerson) {
    doc.text(`Contacto: ${client.contactPerson}`, margin, y);
    y += 5;
  }

  // 4. Email
  doc.text(`Email: ${client.email || ''}`, margin, y);
  y += 5;

  // 5. Teléfono
  doc.text(`Tel: ${client.phone || ''}`, margin, y);
  y += 5;

  // 6. Ciudad del cliente (si existe)
  if (client.city) {
    doc.text(`Ciudad: ${client.city}`, margin, y);
  }
  
  const yClientEnd = y; // Guardamos dónde terminó esta columna

  // --- COLUMNA DERECHA: EVENTO ---
  y = startY; // Reseteamos la altura para empezar la columna derecha arriba
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Evento', midX + 3, y);

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const eventCity = transport.city || '';
  const eventAddress = transport.address || ''; // <--- RECUPERAMOS LA DIRECCIÓN AQUÍ
  const eventSchedule =
    schedule.start && schedule.end
      ? `${schedule.start} – ${schedule.end}`
      : schedule.start || '';

  // 1. Fecha
  doc.text(`Fecha: ${eventDateDisplay || date}`, midX + 3, y);
  y += 5;

  // 2. Ciudad
  doc.text(`Ciudad: ${eventCity}`, midX + 3, y);
  y += 5;

  // 3. Dirección (AHORA SÍ APARECE)
  // Usamos substring para cortar si es demasiado larga y evitar que se salga
  const addressText = eventAddress.length > 35 ? eventAddress.substring(0, 35) + '...' : eventAddress;
  doc.text(`Dir: ${addressText}`, midX + 3, y); 
  y += 5;

  // 4. Horario
  doc.text(`Horario: ${eventSchedule}`, midX + 3, y);

  const yEventEnd = y;

  // --- AJUSTE FINAL DE ALTURA ---
  // Usamos la columna más larga para saber dónde empezar la tabla de productos
  y = Math.max(yClientEnd, yEventEnd) + 10;

  // =====================================================
  //  TABLA DE ÍTEMS  (# primero, luego IMG)
  // =====================================================

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);

  // ENCABEZADOS: # | IMG | Código | Producto | Horas | V. Unit | Subtotal
  doc.text('#', margin, y);
  doc.text('IMG', margin + 10, y);
  doc.text('Código', margin + 28, y);
  doc.text('Producto', margin + 54, y);
  doc.text('Horas', pageWidth - 90, y);             // Antes estaba en -60 (Lo movimos 30mm a la izq)
  doc.text('V. Unit', pageWidth - 55, y, { align: 'right' }); // Antes -42
  doc.text('Subtotal', pageWidth - 20, y, { align: 'right' }); // Antes -22

  y += 4;
  doc.setLineWidth(0.2);
  doc.line(margin, y, pageWidth - margin, y);
  y += 4;

  const rowHeight = 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  for (let i = 0; i < items.length; i++) {
    const it = items[i];

    y = ensureSpace(doc, y, rowHeight + 10);

    // Rectángulo de la fila
    doc.setDrawColor(230);
    doc.rect(margin, y - 4, pageWidth - margin * 2, rowHeight);

    // Imagen + número + datos
    const baseY = y + 2;

    // Número (#) – primera columna
    doc.setFont('helvetica', 'bold');
    doc.text(String(i + 1), margin, baseY);

    // Imagen – segunda columna (a la derecha del número)
    try {
      const imgUrl = it.cover || (it.allImages && it.allImages[0]) || logoDasilva;
      const imgData = await urlToDataURL(imgUrl);
      if (imgData) {
        doc.addImage(
          imgData,
          detectImageType(imgData),
          margin + 6, // un poco a la derecha del número
          y - 2,
          16,
          16
        );
      }
    } catch {}

    // Código – tercera columna
    doc.setFont('helvetica', 'bold');
    doc.text(String(it.productCode || ''), margin + 28, baseY);

    // Producto – cuarta columna (texto multilinea)
    doc.setFont('helvetica', 'normal');
    const nameX = margin + 54;
    const nameWidth = pageWidth - nameX - 95;
    const nameLines = doc.splitTextToSize(it.productName || '', nameWidth);
    doc.text(nameLines, nameX, baseY);
    if (it.itemEventDate) {
      const [, _m, _d] = it.itemEventDate.split('-').map(Number);
      const MESES_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Dia: ${_d} ${MESES_SHORT[_m - 1]}`, nameX, baseY + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
    }

    // Horas + cantidad
    const hours =
      (it.hours ? `${it.hours} h` : '') +
      (it.quantity && it.quantity !== 1 ? ` · x${it.quantity}` : '');
    doc.text(hours || '—', pageWidth - 90, baseY);

  // Valores (Alineados a la DERECHA para que no se salgan)
    doc.text(formatMoney(it.unitPrice), pageWidth - 55, baseY, { align: 'right' });
    doc.text(formatMoney(it.lineTotal), pageWidth - 20, baseY, { align: 'right' });

    y += rowHeight;
  }

  y += 4;

 // =====================================================
  //  TOTALES (CORREGIDO: ALINEACIÓN DERECHA + HORAS EXTRA)
  // =====================================================

  y = ensureSpace(doc, y, 25);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);

  // Coordenadas exactas para alinear con la tabla de arriba
  const xLabel = pageWidth - 90; // Etiquetas a la izquierda
  const xValue = pageWidth - 20; // Números alineados a la derecha (igual que la tabla)

  // 1. Subtotal equipos
  doc.text('Subtotal', xLabel, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatMoney(subtotal), xValue, y, { align: 'right' });

  // 1b. Descuento (si aplica)
  if (discountAmount > 0) {
    y += 6;
    doc.setFont('helvetica', 'bold'); doc.setTextColor(192, 0, 0);
    doc.text(`Descuento (${discountPercent}%)`, xLabel, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`-${formatMoney(discountAmount)}`, xValue, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  // 2. Horas Extra (si aplica — forman parte de la base imponible)
  if (extraCost > 0) {
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(`Horas Extra (${extraHours || 0}h)`, xLabel, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatMoney(extraCost), xValue, y, { align: 'right' });
  }

  y += 6;

  // 3. IVA (sobre subtotal + horas extra; el transporte no lleva IVA)
  doc.setFont('helvetica', 'bold');
  doc.text(`IVA (${ivaPercent}%)`, xLabel, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatMoney(ivaAmount), xValue, y, { align: 'right' });

  // 4. Transporte (sin IVA — solo si tiene costo)
  if ((transport.cost || 0) > 0) {
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Transporte', xLabel, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatMoney(transport.cost), xValue, y, { align: 'right' });
  }

  y += 8;

  // 5. TOTAL FINAL
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('TOTAL', xLabel, y);
  doc.text(formatMoney(total), xValue, y, { align: 'right' });

  y += 10;

  // =====================================================
  //  CONDICIONES
  // =====================================================

  y = ensureSpace(doc, y, 35);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Condiciones:', margin, y);

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const conds =
    conditions.length > 0
      ? conditions
      : [
          'Validez de la oferta: 8 días calendario.',
          'Incluye póliza de Responsabilidad Civil Extracontractual por $300.000.000.',
          'Montaje y operación según especificaciones.',
          'Horario base de alquiler: 5 horas.',
          'Transporte calculado por distancia y volumen.',
          'Pagos: 50% anticipo y 50% contra entrega.',
        ];

  for (const c of conds) {
    y = writeParagraph(doc, '• ' + c, margin, y, pageWidth - margin * 2);
    y += 2;
    y = ensureSpace(doc, y, 15);
  }

  if (notes) {
    y += 4;
    y = ensureSpace(doc, y, 20);
    doc.setFont('helvetica', 'bold');
    doc.text('Notas:', margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    y = writeParagraph(doc, notes, margin, y, pageWidth - margin * 2);
  }

  y += 10;

  // =====================================================
  //  BOTONES (Aprobación / Modificar)
  // =====================================================

  y = ensureSpace(doc, y, 25);

  const btnWidth = 70;
  const btnHeight = 10;
  const btnY = y;

  // Botón azul
  doc.setDrawColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.setFillColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.roundedRect(margin, btnY, btnWidth, btnHeight, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(
    'Aprobación de cotización',
    margin + btnWidth / 2,
    btnY + 6,
    { align: 'center' }
  );

  // Botón naranja
  const btn2X = pageWidth - margin - btnWidth;
  doc.setDrawColor(SECONDARY.r, SECONDARY.g, SECONDARY.b);
  doc.setFillColor(SECONDARY.r, SECONDARY.g, SECONDARY.b);
  doc.roundedRect(btn2X, btnY, btnWidth, btnHeight, 2, 2, 'FD');
  doc.setTextColor(255, 255, 255);
  doc.text('Solicitar cambios', btn2X + btnWidth / 2, btnY + 6, {
    align: 'center',
  });

  const origin =
    (typeof window !== 'undefined' &&
      window.location &&
      window.location.origin) ||
    'http://localhost:3000';
  const qn = encodeURIComponent(quoteNumber);

  doc.link(margin, btnY, btnWidth, btnHeight, {
    url: `${origin}/quote/${qn}/${'accept'}`,
  });
  doc.link(btn2X, btnY, btnWidth, btnHeight, {
    url: `${origin}/quote/${qn}/${'modify'}`,
  });

  y = btnY + btnHeight + 10;

  // =====================================================
  //  PIE DE PÁGINA (webs + redes)
  // =====================================================

  const footerY = 284;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(
    'www.inflablesdasilva.com  -  www.dasilvafunpark.com  •  contactenos@inflablesdasilva.com',
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );

  const iconSize = 5;
  const space = 4;
  const iconsRowY = footerY + 4;

  const icons = [
    {
      img: 'https://cdn-icons-png.flaticon.com/512/733/733585.png',
      url: 'https://api.whatsapp.com/send/?phone=573334008723&text&type=phone_number&app_absent=0',
    },
    {
      img: 'https://cdn-icons-png.flaticon.com/512/733/733547.png',
      url: 'https://www.facebook.com/InflablesDaSilva',
    },
    {
      img: 'https://cdn-icons-png.flaticon.com/512/2111/2111463.png',
      url: 'https://www.instagram.com/inflables_da_silva/',
    },
    {
      img: 'https://cdn-icons-png.flaticon.com/512/3046/3046125.png',
      url: 'https://www.tiktok.com/@inflables_dasilva',
    },
    {
      img: 'https://cdn-icons-png.flaticon.com/512/5968/5968830.png',
      url: 'https://twitter.com/home?lang=es',
    },
    {
      img: 'https://cdn-icons-png.flaticon.com/512/1384/1384060.png',
      url: 'https://www.youtube.com/channel/UCSJ_Y1Zqp5DUkbLBrEUXWOw',
    },
  ];

  const loaded = [];
  for (const icon of icons) {
    const d = await urlToDataURL(icon.img);
    if (d) loaded.push({ d, url: icon.url });
  }

  if (loaded.length > 0) {
    const total =
      loaded.length * iconSize + (loaded.length - 1) * space;
    let x = (pageWidth - total) / 2;

    loaded.forEach((ic) => {
      doc.addImage(ic.d, detectImageType(ic.d), x, iconsRowY, iconSize, iconSize);
      doc.link(x, iconsRowY, iconSize, iconSize, { url: ic.url });
      x += iconSize + space;
    });
  }

  doc.save(`${quoteNumber}.pdf`);
}

export default downloadQuotePDF;
